import { extractQuestionnaireId } from '@seek/rr-caja';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { UTILS_ADD_FORM_DATA } from 'src/app/shared/constants/dispatcherId';
import {
  PAGE_ID_CLASSIFY,
  PAGE_ID_MANAGE,
  PAGE_ID_PAY_AND_POST,
  PAGE_ID_SELECT_AD_TYPE,
  PAGE_ID_WRITE,
} from 'src/app/shared/constants/pageUid';
import {
  FEATURE_CART_AND_PAY_AND_POST_V2,
  FEATURE_LINKOUT,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { log } from 'src/app/utils/logging';
import { dispatchJobLocationActions } from 'src/app/v1/state/dispatcher/jobLocation';
// @ts-ignore
import utils from 'src/app/v1/state/dispatcher/utils';
import { mapJobToDraftValue } from 'src/app/v1/state/dispatcher/utils/addFormData/utils/mapper';
import { actionUpdateProgress } from 'src/app/v1/state/modules/app/progress';
import { actionUpdateBrand } from 'src/app/v1/state/modules/formData/brand';
import { actionUpdateDraftProgress } from 'src/app/v1/state/modules/formData/draftProgress';
import { actionUpdateProductFeatures } from 'src/app/v1/state/modules/session/productFeatures';
import { useDraft } from 'src/app/v1/views/hooks/useDraft';
import { useQueryParams } from 'src/app/v1/views/hooks/useQueryParams';
import { useQuestionnaire } from 'src/app/v1/views/hooks/useQuestionnaire';
import { useGetJobDetailsLazyQuery } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { MappedFormData } from 'src/types/draft';

import { useCanUpdateExpiryDate } from '../useCanUpdateExpiryDate';

export const useCopyJob = () => {
  const { advertiserNation, isDataCleanupComplete, seekAdvertiserId } =
    useSelector(
      (state: AppState) => ({
        advertiserNation: state.session.hirerAccount.nation,
        isDataCleanupComplete: state.app.isDataCleanupComplete,
        seekAdvertiserId: state.session.seekAdvertiserId,
      }),
      shallowEqual,
    );

  const hasLinkOutEnabled = useFeatureToggleData(FEATURE_LINKOUT);
  const isCartEnabled = useFeatureToggleData(FEATURE_CART_AND_PAY_AND_POST_V2);
  const { cartState } = useShoppingCart();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const setQuestionnaire = useQuestionnaire();
  const { getJobIdParam } = useQueryParams();
  const { createDraft } = useDraft();
  const canUpdateExpiryDate = useCanUpdateExpiryDate();

  const populateData = async (data: any) => {
    if (data) {
      // formData
      const mappedDraftData = mapJobToDraftValue({
        jobData: data.getJobDetails,
        advertiserNation,
        hasLinkOutEnabled: Boolean(hasLinkOutEnabled),
        flowType: 'create',
        canUpdateExpiryDate,
      });

      if ((mappedDraftData as MappedFormData).expiryDate) {
        log.error('useCopyJob | Copy Job with ExpiryDate', {
          expiryDate: (mappedDraftData as MappedFormData).expiryDate,
          advertiserNation,
          hasLinkOutEnabled,
          isDataCleanupComplete,
          seekAdvertiserId,
        });
      }

      await dispatch(utils(UTILS_ADD_FORM_DATA, mappedDraftData));
      // brand
      dispatch(actionUpdateBrand(data.getJobDetails?.branding));

      createDraft();

      // questionnaire
      const extractedQuestionnaireId = extractQuestionnaireId(
        data?.getJobDetails?.questionnaireUrl,
      );
      await setQuestionnaire(extractedQuestionnaireId);

      // location
      dispatchJobLocationActions(dispatch, data.getJobDetails?.locations?.[0]);

      dispatch(
        actionUpdateProductFeatures(data?.getJobDetails?.productFeatures),
      );

      const hasProductBeenSelected = isCartEnabled
        ? Boolean(cartState?.items && cartState?.items.length > 0)
        : true;

      // progress
      dispatch(
        actionUpdateProgress({
          currentPageId: PAGE_ID_CLASSIFY,
          furthestPageId: PAGE_ID_PAY_AND_POST,
          isValidObj: {
            [PAGE_ID_CLASSIFY]: true,
            [PAGE_ID_SELECT_AD_TYPE]: hasProductBeenSelected,
            [PAGE_ID_WRITE]: true,
            [PAGE_ID_MANAGE]: true,
            [PAGE_ID_PAY_AND_POST]: true,
          },
        }),
      );
      dispatch(actionUpdateDraftProgress(PAGE_ID_CLASSIFY));
      setIsLoading(false);
    }
  };

  const [getJobDetails, { data, error }] = useGetJobDetailsLazyQuery({
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (data) {
      populateData(data);
    }

    if (error) {
      log.error('useCopyJob | GetJobDetails', {
        error,
        advertiserNation,
        hasLinkOutEnabled,
        isDataCleanupComplete,
        seekAdvertiserId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error]);

  useEffect(() => {
    const jobIdParam = getJobIdParam();

    if (jobIdParam) {
      if (isDataCleanupComplete && seekAdvertiserId) {
        getJobDetails({
          variables: {
            advertiserId: seekAdvertiserId,
            jobId: jobIdParam,
          },
        });
      }
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataCleanupComplete, seekAdvertiserId]);

  return {
    loading: isLoading,
  };
};
